import React, { useState, useRef } from 'react';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { compressImage, createThumbnail, generateUniqueFileName, createImageData } from '../../utils/imageUtils';
import { uploadFile, createImagePath, createFileMetadata } from '../../utils/storageUtils';
import { checkAllDuplicateMethods, generateFileHash } from '../../utils/duplicateImageUtils';

const ImageUploadModal = ({ isOpen, onClose, girlId, currentImages, maxImages, onUploadSuccess, pendingImages = [], rejectedImages = [] }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  // ตรวจสอบจำนวนรูปที่สามารถอัพโหลดได้
  const remainingSlots = maxImages === -1 ? -1 : maxImages - currentImages.length;
  const canUploadMore = maxImages === -1 || remainingSlots > 0;
  
  // ตรวจสอบรูปซ้ำใน currentImages
  const hasDuplicates = currentImages.some((img, index) => 
    currentImages.slice(index + 1).some(otherImg => 
      img.fileHash === otherImg.fileHash || 
      img.url === otherImg.url ||
      (img.originalName === otherImg.originalName && img.originalSize === otherImg.originalSize)
    )
  );

  // Debug logs
  console.log('ImageUploadModal Debug:');
  console.log('- maxImages:', maxImages);
  console.log('- currentImages.length:', currentImages.length);
  console.log('- remainingSlots:', remainingSlots);
  console.log('- canUploadMore:', canUploadMore);
  console.log('- selectedFiles.length:', selectedFiles.length);

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);
    const validFiles = [];
    
    console.log('🔍 File selection started:', {
      filesCount: fileArray.length,
      maxImages,
      currentImages: currentImages.length,
      remainingSlots: maxImages === -1 ? -1 : maxImages - currentImages.length
    });
    
    for (const file of fileArray) {
      console.log(`🔍 Processing file: ${file.name} (${file.size} bytes)`);
      
      // ตรวจสอบประเภทไฟล์
      if (!file.type.startsWith('image/')) {
        alert(`ไฟล์ ${file.name} ไม่ใช่ไฟล์รูปภาพ`);
        continue;
      }
      
      // ตรวจสอบขนาดไฟล์ (ไม่เกิน 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert(`ไฟล์ ${file.name} มีขนาดใหญ่เกินไป (ไม่เกิน 5MB)`);
        continue;
      }
      
      // ตรวจสอบรูปซ้ำกับรูปที่มีอยู่แล้ว
      console.log(`🔍 Checking file for duplicates: ${file.name}`);
      
      // ตรวจสอบกับรูปที่อนุมัติแล้ว
      const isDuplicateWithApproved = currentImages.some(existingImg => 
        existingImg.originalName === file.name && 
        existingImg.originalSize === file.size
      );
      
      if (isDuplicateWithApproved) {
        console.log(`❌ Duplicate found with approved image: ${file.name}`);
        alert(`รูปภาพ ${file.name} มีอยู่แล้วในระบบ (รูปที่อนุมัติแล้ว)`);
        continue;
      }
      
      // ตรวจสอบกับรูปที่รออนุมัติ
      const isDuplicateWithPending = pendingImages.some(existingImg => 
        existingImg.originalName === file.name && 
        existingImg.originalSize === file.size
      );
      
      if (isDuplicateWithPending) {
        console.log(`❌ Duplicate found with pending image: ${file.name}`);
        alert(`รูปภาพ ${file.name} มีอยู่แล้วในระบบ (รอการอนุมัติ)`);
        continue;
      }
      
      // ตรวจสอบกับรูปที่ปฏิเสธ
      const isDuplicateWithRejected = rejectedImages.some(existingImg => 
        existingImg.originalName === file.name && 
        existingImg.originalSize === file.size
      );
      
      if (isDuplicateWithRejected) {
        console.log(`❌ Duplicate found with rejected image: ${file.name}`);
        alert(`รูปภาพ ${file.name} มีอยู่แล้วในระบบ (รูปที่ปฏิเสธ)`);
        continue;
      }
      
      console.log(`✅ File is unique: ${file.name}`);
      validFiles.push(file);
    }

    // จำกัดจำนวนไฟล์ตามที่เหลือ (ถ้าไม่ใช่ไม่จำกัด)
    const filesToAdd = remainingSlots === -1 ? validFiles : validFiles.slice(0, remainingSlots);
    
    console.log('📊 Files to add:', {
      original: fileArray.length,
      valid: validFiles.length,
      limited: filesToAdd.length
    });
    
    setSelectedFiles(prev => [...prev, ...filesToAdd]);
  };


  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files);
    }
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadImages = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      console.log('🚀 Image Upload Process Started:');
      console.log('- Selected Files Count:', selectedFiles.length);
      console.log('- Girl ID:', girlId);
      console.log('- Max Images:', maxImages);
      console.log('- Current Images:', currentImages.length);
      console.log('- Remaining Slots:', remainingSlots);
      console.log('- Can Upload More:', canUploadMore);
      
      const uploadPromises = selectedFiles.map(async (file, index) => {
        console.log(`Processing file ${index + 1}/${selectedFiles.length}:`, file.name);
        setUploadProgress((index / selectedFiles.length) * 50); // 50% สำหรับการบีบอัด
        
        // บีบอัดรูปภาพ
        const compressedFile = await compressImage(file, 800, 600, 0.8);
        
        setUploadProgress(50 + (index / selectedFiles.length) * 30); // 30% สำหรับการสร้าง thumbnail
        
        // สร้าง thumbnail
        const thumbnail = await createThumbnail(compressedFile, 150);
        
        setUploadProgress(80 + (index / selectedFiles.length) * 20); // 20% สำหรับการอัพโหลด
        
        // สร้างชื่อไฟล์ที่ไม่ซ้ำ
        const fileName = generateUniqueFileName(girlId, file.name);
        const storagePath = createImagePath(girlId, 'pending', fileName);
        
        // สร้าง metadata
        const metadata = createFileMetadata(compressedFile, {
          originalName: file.name,
          originalSize: file.size,
          compressedSize: compressedFile.size,
          compressionRatio: Math.round((1 - compressedFile.size / file.size) * 100)
        });
        
        // อัพโหลดไฟล์ที่บีบอัดแล้ว (พร้อม retry)
        let uploadResult;
        let retryCount = 0;
        const maxRetries = 5; // เพิ่มจำนวนครั้งที่ลอง
        
        while (retryCount < maxRetries) {
          try {
            console.log(`Upload attempt ${retryCount + 1}/${maxRetries} for file:`, file.name);
            console.log('Storage path:', storagePath);
            console.log('File size:', compressedFile.size, 'bytes');
            
            // เพิ่ม timeout สำหรับการอัพโหลด
            const uploadPromise = uploadFile(compressedFile, storagePath, metadata);
            const timeoutPromise = new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Upload timeout')), 30000) // 30 วินาที timeout
            );
            
            uploadResult = await Promise.race([uploadPromise, timeoutPromise]);
            console.log('Upload successful for file:', file.name);
            break; // สำเร็จแล้ว ออกจาก loop
          } catch (error) {
            retryCount++;
            console.log(`Upload attempt ${retryCount} failed for file ${file.name}:`, error.message);
            console.error('Full error:', error);
            
            if (retryCount >= maxRetries) {
              throw new Error(`อัพโหลดล้มเหลวหลังจากลอง ${maxRetries} ครั้ง: ${error.message}`);
            }
            
            // รอเพิ่มขึ้นตามจำนวนครั้งที่ลอง (exponential backoff)
            const waitTime = Math.min(1000 * Math.pow(2, retryCount - 1), 10000); // สูงสุด 10 วินาที
            console.log(`Retrying in ${waitTime}ms... (${retryCount}/${maxRetries})`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }
        
        // สร้าง hash ของไฟล์
        const fileHash = await generateFileHash(file);
        
        // สร้างข้อมูลรูปภาพ
        const imageData = createImageData(uploadResult.url, fileName, file.name, 'pending');
        
        return {
          ...imageData,
          thumbnail,
          originalSize: file.size,
          compressedSize: compressedFile.size,
          compressionRatio: Math.round((1 - compressedFile.size / file.size) * 100),
          fileHash: fileHash
        };
      });

      const uploadedImages = await Promise.all(uploadPromises);
      
      // อัพเดทข้อมูลใน Firestore
      console.log('💾 Saving to database...');
      const girlRef = doc(db, 'girls', girlId);
      
      // ดึงข้อมูลปัจจุบันก่อน
      const girlDoc = await getDoc(girlRef);
      const currentData = girlDoc.data();
      const currentPendingImages = currentData.pendingImages || [];
      
      console.log('📊 Current pending images:', currentPendingImages.length);
      console.log('📊 New images to add:', uploadedImages.length);
      
      await updateDoc(girlRef, {
        pendingImages: arrayUnion(...uploadedImages),
        lastUpdated: new Date().toISOString()
      });
      
      console.log('✅ Database updated successfully');
      
      // ตรวจสอบผลลัพธ์
      const updatedGirlDoc = await getDoc(girlRef);
      const updatedData = updatedGirlDoc.data();
      const updatedPendingImages = updatedData.pendingImages || [];
      
      console.log('📊 Final pending images count:', updatedPendingImages.length);

      setUploadProgress(100);
      
      // รอสักครู่เพื่อให้ progress bar แสดงผล
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onUploadSuccess(uploadedImages);
      
      // รีเซ็ตฟอร์ม
      setSelectedFiles([]);
      setUploadProgress(0);
      
      alert('อัพโหลดรูปภาพเรียบร้อยแล้ว! รอการอนุมัติจากแอดมิน');
      onClose();
      
    } catch (error) {
      console.error('Error uploading images:', error);
      
      // แสดง error message ที่เฉพาะเจาะจง
      let errorMessage = 'เกิดข้อผิดพลาดในการอัพโหลด กรุณาลองใหม่อีกครั้ง';
      
      if (error.message.includes('CORS')) {
        errorMessage = 'เกิดข้อผิดพลาด CORS กรุณาลองใหม่อีกครั้ง หรือติดต่อแอดมิน';
      } else if (error.message.includes('unauthorized')) {
        errorMessage = 'ไม่มีสิทธิ์ในการอัพโหลด กรุณาล็อกอินใหม่';
      } else if (error.message.includes('network') || error.message.includes('ERR_FAILED')) {
        errorMessage = 'เกิดข้อผิดพลาดเครือข่าย กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      } else if (error.message.includes('timeout') || error.message.includes('Timeout')) {
        errorMessage = 'การอัพโหลดใช้เวลานานเกินไป กรุณาลองใหม่อีกครั้ง';
      } else if (error.message.includes('size')) {
        errorMessage = 'ไฟล์มีขนาดใหญ่เกินไป กรุณาเลือกไฟล์ที่เล็กกว่า';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'ไม่สามารถเชื่อมต่อกับเซิร์ฟเวอร์ได้ กรุณาตรวจสอบการเชื่อมต่ออินเทอร์เน็ต';
      }
      
      alert(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">อัพโหลดรูปภาพ</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl"
            disabled={uploading}
          >
            ×
          </button>
        </div>

        {/* ข้อมูล Tier */}
        <div className="bg-blue-50 p-3 rounded-lg mb-4">
          <p className="text-sm text-blue-700">
            <strong>สิทธิ์การอัพโหลด:</strong> สามารถอัพโหลดได้ {maxImages === -1 ? 'ไม่จำกัด' : `${maxImages} รูป`}
          </p>
          <p className="text-sm text-blue-700">
            <strong>รูปที่อัพโหลดแล้ว:</strong> {currentImages.length} รูป
          </p>
          <p className="text-sm text-blue-700">
            <strong>สามารถอัพโหลดเพิ่มได้:</strong> {remainingSlots === -1 ? 'ไม่จำกัด' : `${remainingSlots} รูป`}
          </p>
        </div>

        {!canUploadMore && (
          <div className="bg-red-50 p-3 rounded-lg mb-4">
            <p className="text-sm text-red-700">
              คุณอัพโหลดรูปครบจำนวนสูงสุดแล้ว กรุณาอัพเกรด Tier เพื่ออัพโหลดรูปเพิ่มเติม
            </p>
          </div>
        )}

        {hasDuplicates && (
          <div className="bg-yellow-50 p-3 rounded-lg mb-4">
            <p className="text-sm text-yellow-700">
              ⚠️ พบรูปภาพซ้ำในระบบ กรุณาตรวจสอบและทำความสะอาดก่อนอัพโหลดรูปใหม่
            </p>
          </div>
        )}

        {/* พื้นที่ลากวาง */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center mb-4 transition-colors ${
            dragActive 
              ? 'border-blue-500 bg-blue-50' 
              : 'border-gray-300 hover:border-gray-400'
          } ${!canUploadMore ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => canUploadMore && fileInputRef.current?.click()}
        >
          <div className="text-4xl mb-2">📸</div>
          <p className="text-lg font-medium text-gray-700 mb-2">
            {canUploadMore ? 'ลากรูปมาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์' : 'ไม่สามารถอัพโหลดเพิ่มได้'}
          </p>
          <p className="text-sm text-gray-500">
            รองรับไฟล์: JPG, PNG, GIF (ขนาดไม่เกิน 5MB)
          </p>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInputChange}
            className="hidden"
            disabled={!canUploadMore}
          />
        </div>

        {/* รายการไฟล์ที่เลือก */}
        {selectedFiles.length > 0 && (
          <div className="mb-4">
            <h3 className="text-lg font-medium mb-2">ไฟล์ที่เลือก ({selectedFiles.length} ไฟล์)</h3>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                  <div className="flex items-center space-x-2 flex-1">
                    <span className="text-sm">📷</span>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm truncate">{file.name}</div>
                      <div className="text-xs text-gray-500">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                        {file.type && (
                          <span className="ml-2 text-blue-600">
                            → จะบีบอัดเป็น ~{Math.round(file.size * 0.3 / 1024 / 1024 * 100) / 100} MB
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2 ml-2">
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700 text-sm"
                      disabled={uploading}
                    >
                      ลบ
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* ข้อมูลการบีบอัด */}
            <div className="mt-2 p-2 bg-blue-50 rounded text-sm text-blue-700">
              <div className="flex items-center space-x-2">
                <span>ℹ️</span>
                <span>รูปภาพจะถูกบีบอัดอัตโนมัติเพื่อประหยัดพื้นที่และเพิ่มความเร็วในการโหลด</span>
              </div>
            </div>
          </div>
        )}

        {/* Progress Bar */}
        {uploading && (
          <div className="mb-4">
            <div className="flex justify-between text-sm text-gray-600 mb-1">
              <span>กำลังอัพโหลด...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress}%` }}
              ></div>
            </div>
          </div>
        )}

        {/* ปุ่ม */}
        <div className="flex space-x-3">
          <button
            onClick={onClose}
            disabled={uploading}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            ยกเลิก
          </button>
          <button
            onClick={uploadImages}
            disabled={selectedFiles.length === 0 || uploading}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {uploading ? 'กำลังอัพโหลด...' : `อัพโหลด ${selectedFiles.length} รูป`}
          </button>
        </div>
      </div>

    </div>
  );
};

export default ImageUploadModal;
